import { useCallback, useState } from 'react';
import { apiAuth } from '../services/api.ts';
import { solvePow } from '../services/powClient.ts';
import { useSession } from '../state/session.store.ts';

interface PowState {
  status: 'idle' | 'fetching' | 'solving' | 'verifying' | 'done' | 'error';
  hashesPerSec: number;
  progress: number;
  error: string | null;
}

const initial: PowState = {
  status: 'idle',
  hashesPerSec: 0,
  progress: 0,
  error: null,
};

export function usePoW() {
  const [state, setState] = useState<PowState>(initial);
  const setSession = useSession((s) => s.set);

  const run = useCallback(async (accessJwt?: string): Promise<boolean> => {
    setState({ ...initial, status: 'fetching' });
    try {
      const ch = await apiAuth.challenge();
      setState((s) => ({ ...s, status: 'solving' }));
      const sol = await solvePow(ch.prefix, ch.difficulty, (p) => {
        setState((s) => ({ ...s, hashesPerSec: p.hashesPerSec, progress: Math.min(95, s.progress + 1) }));
      });
      setState((s) => ({ ...s, status: 'verifying', progress: 98 }));
      const v = await apiAuth.verify({
        challengeId: ch.challengeId,
        nonce: sol.nonce,
        ...(accessJwt ? { accessJwt } : {}),
      });
      setSession({ userId: v.userId, token: v.sessionToken, ranked: v.ranked });
      setState({ status: 'done', hashesPerSec: 0, progress: 100, error: null });
      return true;
    } catch (err) {
      setState({
        status: 'error',
        hashesPerSec: 0,
        progress: 0,
        error: err instanceof Error ? err.message : 'unknown',
      });
      return false;
    }
  }, [setSession]);

  return { ...state, run };
}
